import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
  HARBOUR_NEWSLETTER_ACTION,
  HARBOUR_TIMEZONE,
  formatHarbourNewsletterBriefClipboard,
  getHarbourNewsletterBriefForCycle,
  getHarbourNewsletterNextCycleKey,
  isHarbourNewsletterAction,
  parseHarbourNewsletterCycleKey,
} from 'src/constants/harbour'
import { copyHarbourText } from 'src/utils/harbourPhone'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Monthly CS newsletter reminder prefs. Per account so shared browsers
 * do not mix We sent this / Next month / Skip this topic.
 * Harbour does not send mail.
 */
export const HARBOUR_NEWSLETTER_PREFS_KEY_PREFIX = 'harbour.newsletter'

const getStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null
  }
  return window.localStorage
}

/**
 * UK calendar month for the CS newsletter reminder.
 * Same Europe/London clock as owner / plan chips.
 * @param {Date|string|number} [now]
 * @returns {string} `YYYY-MM`
 */
export function getHarbourNewsletterCycleKey(now = new Date()) {
  return dayjs.tz(now, HARBOUR_TIMEZONE).format('YYYY-MM')
}

/**
 * Brief for this UK month, no skip offset. Harbour does not send.
 * @param {Date|string|number} [now]
 */
export function getHarbourNewsletterBriefForNow(now = new Date()) {
  return getHarbourNewsletterBriefForCycle(getHarbourNewsletterCycleKey(now))
}

/**
 * @param {string|null|undefined} accountId
 * @returns {string|null}
 */
export function buildHarbourNewsletterPrefsStorageKey(accountId) {
  const id = String(accountId || '').trim()
  if (!id) {
    return null
  }
  return `${HARBOUR_NEWSLETTER_PREFS_KEY_PREFIX}.${id}`
}

function readRawPrefs(accountId, storage) {
  const key = buildHarbourNewsletterPrefsStorageKey(accountId)
  if (!key || !storage) {
    return null
  }

  try {
    const raw = storage.getItem(key)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function persistPrefs(accountId, prefs, storage) {
  const key = buildHarbourNewsletterPrefsStorageKey(accountId)
  if (!key || !storage) {
    return false
  }

  try {
    storage.setItem(key, JSON.stringify(prefs))
    return true
  } catch {
    return false
  }
}

function normalizeSkipOffset(value) {
  if (!Number.isFinite(Number(value))) {
    return 0
  }
  return Math.max(0, Math.floor(Number(value)))
}

/**
 * Skip offset only lasts this UK month. Hidden until `hiddenUntilCycle`
 * (exclusive floor: visible when current cycle >= that key).
 * @param {object|null} raw
 * @param {string} cycleKey
 */
export function resolveHarbourNewsletterPrefs(raw, cycleKey) {
  const cycle = parseHarbourNewsletterCycleKey(cycleKey)
    ? String(cycleKey).trim()
    : getHarbourNewsletterCycleKey()
  const storedCycle = parseHarbourNewsletterCycleKey(raw?.cycleKey)
    ? String(raw.cycleKey).trim()
    : null
  const sameCycle = storedCycle === cycle
  const skipOffset = sameCycle ? normalizeSkipOffset(raw?.skipOffset) : 0
  const hiddenUntilCycle = parseHarbourNewsletterCycleKey(raw?.hiddenUntilCycle)
    ? String(raw.hiddenUntilCycle).trim()
    : null
  const visible = !hiddenUntilCycle || cycle >= hiddenUntilCycle
  const lastAction =
    sameCycle && isHarbourNewsletterAction(raw?.lastAction)
      ? raw.lastAction
      : null

  return {
    cycleKey: cycle,
    skipOffset,
    hiddenUntilCycle,
    lastAction,
    visible,
  }
}

function loadResolvedPrefs(accountId, storage, now) {
  const cycleKey = getHarbourNewsletterCycleKey(now)
  return resolveHarbourNewsletterPrefs(
    readRawPrefs(accountId, storage),
    cycleKey,
  )
}

/**
 * @param {string|null|undefined} accountId
 * @param {object} [storage]
 * @param {Date|string|number} [now]
 */
export function isHarbourNewsletterReminderVisible(
  accountId,
  storage = getStorage(),
  now = new Date(),
) {
  return loadResolvedPrefs(accountId, storage, now).visible
}

/**
 * Visible or not, returns this month's brief (with skip offset).
 * @param {string|null|undefined} accountId
 * @param {object} [storage]
 * @param {Date|string|number} [now]
 */
export function getHarbourNewsletterReminderState(
  accountId,
  storage = getStorage(),
  now = new Date(),
) {
  const prefs = loadResolvedPrefs(accountId, storage, now)
  return {
    ...prefs,
    brief: getHarbourNewsletterBriefForCycle(prefs.cycleKey, prefs.skipOffset),
  }
}

function hideUntilNextMonth(accountId, lastAction, storage, now) {
  const prefs = loadResolvedPrefs(accountId, storage, now)
  const hiddenUntilCycle = getHarbourNewsletterNextCycleKey(prefs.cycleKey)
  if (!hiddenUntilCycle) {
    return false
  }
  return persistPrefs(
    accountId,
    {
      cycleKey: prefs.cycleKey,
      skipOffset: prefs.skipOffset,
      hiddenUntilCycle,
      lastAction,
    },
    storage,
  )
}

/** We sent this — hide until the next UK month. Harbour still does not send. */
export function markHarbourNewsletterSent(
  accountId,
  storage = getStorage(),
  now = new Date(),
) {
  return hideUntilNextMonth(
    accountId,
    HARBOUR_NEWSLETTER_ACTION.SENT,
    storage,
    now,
  )
}

/** Remind me next month — same hide; they skipped sending. */
export function remindHarbourNewsletterNextMonth(
  accountId,
  storage = getStorage(),
  now = new Date(),
) {
  return hideUntilNextMonth(
    accountId,
    HARBOUR_NEWSLETTER_ACTION.LATER,
    storage,
    now,
  )
}

/** Skip this topic — next brief for the rest of this UK month. Still visible. */
export function skipHarbourNewsletterTopic(
  accountId,
  storage = getStorage(),
  now = new Date(),
) {
  const prefs = loadResolvedPrefs(accountId, storage, now)
  if (!prefs.visible) {
    return false
  }
  return persistPrefs(
    accountId,
    {
      cycleKey: prefs.cycleKey,
      skipOffset: prefs.skipOffset + 1,
      hiddenUntilCycle: null,
      lastAction: HARBOUR_NEWSLETTER_ACTION.SKIP,
    },
    storage,
  )
}

/**
 * Audience + benefits + Kev P.S. Harbour does not send.
 * @param {object} [brief]
 */
export async function copyHarbourNewsletterBrief(brief = {}) {
  return copyHarbourText(
    formatHarbourNewsletterBriefClipboard(brief),
    'No brief to copy',
  )
}
